/**
 * /tools/539 — 539 工具完整集 Hub 頁面
 *
 * SEO 角色：539 工具集群的 Pillar of Tools
 * 目標關鍵字：539 工具、今彩 539 工具、539 免費工具、539 開獎查詢工具
 * 設計目標：集中 12 個 539 工具的內部連結，最大化 PageRank 內部流動
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Activity,
  History,
  TrendingUp,
  BarChart3,
  CalendarDays,
  Sparkles,
  Calculator,
  DollarSign,
  CheckCircle,
  Search,
  BookOpen,
  Layers,
  Trophy,
  FileText,
} from 'lucide-react';
import SEOHead from '../../../seo/SEOHead';
import { buildBreadcrumb } from '../../../seo/buildBreadcrumb';
import { trackEvent } from '../../../utils/tracking';
import { db } from '../../../firebase';
import { doc, getDoc } from 'firebase/firestore';


const COLLECTION_PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '今彩 539 完整工具集',
  url: 'https://cyxey.uk/tools/539',
  description:
    'cyxey.uk 提供 12 款免費 539 工具：即時開獎、歷史查詢、走勢圖、冷熱號分析、智慧選號、包牌計算、對獎機器人。每日 20:35 自動更新。',
  inLanguage: 'zh-TW',
};

const ITEM_LIST_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListOrder: 'https://schema.org/ItemListOrderAscending',
  numberOfItems: 12,
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '539 即時開獎', url: 'https://cyxey.uk/tools/539-live-draw' },
    { '@type': 'ListItem', position: 2, name: '539 歷史開獎查詢', url: 'https://cyxey.uk/tools/539-history' },
    { '@type': 'ListItem', position: 3, name: '539 走勢圖', url: 'https://cyxey.uk/tools/539-trend-chart' },
    { '@type': 'ListItem', position: 4, name: '539 統計分析儀表板', url: 'https://cyxey.uk/tools/539-statistics' },
    { '@type': 'ListItem', position: 5, name: '539 月度統計報告', url: 'https://cyxey.uk/tools/539-monthly-report' },
    { '@type': 'ListItem', position: 6, name: '539 選號分析器', url: 'https://cyxey.uk/tools/539-number-analyzer' },
    { '@type': 'ListItem', position: 7, name: '539 智慧選號器', url: 'https://cyxey.uk/tools/539-number-generator' },
    { '@type': 'ListItem', position: 8, name: '539 碰數計算機（連碰立柱天二天三）', url: 'https://cyxey.uk/tools/539-combo-calculator' },
    { '@type': 'ListItem', position: 9, name: '539 包牌計算器', url: 'https://cyxey.uk/tools/539-calculator' },
    { '@type': 'ListItem', position: 10, name: '539 獎金計算器', url: 'https://cyxey.uk/tools/539-payout-calculator' },
    { '@type': 'ListItem', position: 11, name: '539 對獎機器人', url: 'https://cyxey.uk/tools/539-checker' },
    { '@type': 'ListItem', position: 12, name: '539 開獎單下載（白單／紙張 A4 列印）', url: 'https://cyxey.uk/tools/539-draw-sheet' },
  ],
};

const BREADCRUMB_SCHEMA = buildBreadcrumb([
  { name: '首頁', path: '/' },
  { name: '工具中心', path: '/tools' },
  { name: '539 完整工具集', path: '/tools/539' },
]);

interface ToolCard {
  href: string;
  title: string;
  desc: string;
  badge?: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const GROUPS: { title: string; icon: React.ComponentType<{ className?: string }>; tools: ToolCard[] }[] = [
  {
    title: '開獎查詢',
    icon: Activity,
    tools: [
      {
        href: '/tools/539-live-draw',
        title: '539 即時開獎',
        desc: '每晚 20:30 開獎倒數＋當期最新號碼，含對獎與冷熱號',
        badge: '每日更新',
        Icon: Activity,
      },
      {
        href: '/tools/539-history',
        title: '539 歷史開獎查詢',
        desc: '375 期完整紀錄，支援日期範圍、號碼搜尋、進階排序',
        Icon: History,
      },
      {
        href: '/tools/539-draw-sheet',
        title: '539 開獎單下載',
        desc: '539 白單／紙張 A4 列印一覽表，4 種版型 PDF／PNG／列印一鍵下載',
        badge: '旗艦',
        Icon: FileText,
      },
      {
        href: '/tools/539-annual-archive',
        title: '539 年度開獎彙整',
        desc: '依年份彙整的完整開獎紀錄，可回溯逐年號碼與統計',
        Icon: History,
      },
      {
        href: '/tools/539-master-dashboard',
        title: '539 總覽儀表板',
        desc: '把開獎、走勢、冷熱號與統計指標集中在同一頁',
        Icon: BarChart3,
      },
    ],
  },
  {
    title: '數據分析',
    icon: BarChart3,
    tools: [
      {
        href: '/tools/539-trend-chart',
        title: '539 走勢圖',
        desc: '近 30/50/100 期號碼走勢矩陣＋尾數＋區間分析',
        Icon: TrendingUp,
      },
      {
        href: '/tools/539-statistics',
        title: '539 統計分析儀表板',
        desc: '冷熱號 / 遺漏值 / 奇偶比 / 和值 / 連號八大維度',
        Icon: BarChart3,
      },
      {
        href: '/tools/539-monthly-report',
        title: '539 月度統計報告',
        desc: '本月開獎期數、冷熱排行 Top10、與上月比較',
        badge: '獨家',
        Icon: CalendarDays,
      },
      {
        href: '/tools/539-number-analyzer',
        title: '539 選號分析器',
        desc: '冷熱排行、尾數頻率、奇偶比，含真實 100 期 Firestore 數據',
        Icon: Search,
      },
    ],
  },
  {
    title: '投注計算',
    icon: Calculator,
    tools: [
      {
        href: '/tools/539-number-generator',
        title: '539 智慧選號器',
        desc: '4 種模式：純隨機、熱號優先、冷號補位、自訂條件',
        Icon: Sparkles,
      },
      {
        href: '/tools/539-combo-calculator',
        title: '539 碰數計算機',
        desc: '連碰、立柱、天二、天三、拖膽 5 種玩法成本一鍵算',
        badge: '新',
        Icon: Layers,
      },
      {
        href: '/tools/539-calculator',
        title: '539 包牌計算器',
        desc: '二星、三星、四星包牌組數與成本',
        Icon: Calculator,
      },
      {
        href: '/tools/539-payout-calculator',
        title: '539 獎金計算器',
        desc: '地下 539 賠率試算、二三四星中獎金額一秒算',
        Icon: DollarSign,
      },
    ],
  },
  {
    title: '對獎工具',
    icon: CheckCircle,
    tools: [
      {
        href: '/tools/539-checker',
        title: '539 對獎機器人',
        desc: '輸入號碼自動比對近期開獎，顯示中獎等級與獎金試算',
        Icon: CheckCircle,
      },
    ],
  },
];

interface LatestDraw {
  period: string;
  drawDate: string;
  numbers: number[];
}

function getBallColor(n: number): string {
  if (n <= 10) return 'bg-red-600';
  if (n <= 20) return 'bg-blue-600';
  if (n <= 30) return 'bg-emerald-600';
  return 'bg-purple-600';
}

export default function Lottery539HubPage() {
  const [latest, setLatest] = useState<LatestDraw | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'lottery539_stats', 'latest'));
        if (!snap.exists()) return;
        const d = snap.data() as { latestDraw?: LatestDraw };
        if (!cancelled && d.latestDraw) setLatest(d.latestDraw);
      } catch {
        /* fallback gracefully */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <SEOHead
        title="539計算機總匯｜包牌·連碰·立柱·獎金一站免費試算"
        description="539計算機/計算器一站總匯：包牌組數、連碰立柱、獎金賠率全玩法免費試算，另含即時開獎、走勢圖、冷熱號分析、智慧選號、對獎機器人 12 款 539 工具。每日 20:35 自動更新，完全免費。"
        canonical="https://cyxey.uk/tools/539"
        jsonLd={[COLLECTION_PAGE_SCHEMA, ITEM_LIST_SCHEMA, BREADCRUMB_SCHEMA]}
      />

      <div className="min-h-screen bg-[#050505] text-gray-200">
        {/* ── Nav ── */}
        <nav className="max-w-5xl mx-auto px-4 pt-6 pb-2">
          <Link
            to="/tools"
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            返回工具中心
          </Link>
        </nav>

        <main className="max-w-5xl mx-auto px-4 py-8">
          {/* ── Hero ── */}
          <header className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] px-2.5 py-0.5 rounded-full border border-yellow-500/30 text-[#D4AF37] font-medium">
                Pillar · 工具集
              </span>
              <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-yellow-500/10 text-[#F4C430] font-medium">
                12 款免費
              </span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
              今彩 539 計算機與完整工具集
            </h1>
            <p className="text-sm text-gray-400 leading-relaxed max-w-3xl">
              cyxey.uk 自製的 12 款 539 工具，整合
              <strong className="text-white">即時開獎、走勢分析、選號計算、對獎驗證</strong>
              四大應用場景。所有工具完全免費、無需註冊、每日 20:35 開獎後 5 分鐘內自動更新。
              全站用真實
              <strong className="text-white">375+ 期 Firestore 後台數據</strong>
              ，非模擬資料。
            </p>
          </header>

          {/* ── Latest Draw Card ── */}
          {latest && (
            <section className="mb-10 p-5 rounded-2xl bg-gradient-to-br from-[#1a1206] to-[#0a0a0a] border border-yellow-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-[#F4C430]" />
                <span className="text-xs text-[#D4AF37] font-bold">最新一期</span>
                <span className="text-xs text-gray-500">第 {latest.period} 期 · {latest.drawDate}</span>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {latest.numbers.map((n) => (
                  <span
                    key={n}
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white ${getBallColor(n)} shadow-lg`}
                  >
                    {n.toString().padStart(2, '0')}
                  </span>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to="/tools/539-checker"
                  className="text-xs text-[#F4C430] hover:underline"
                  onClick={() => trackEvent('539_hub_to_tool_click', { tool: 'checker' })}
                >
                  → 立即對獎
                </Link>
                <Link
                  to="/tools/539-live-draw"
                  className="text-xs text-[#F4C430] hover:underline"
                  onClick={() => trackEvent('539_hub_to_tool_click', { tool: 'live-draw' })}
                >
                  → 即時開獎詳情
                </Link>
              </div>
            </section>
          )}

          {/* ── Tool Groups ── */}
          {GROUPS.map((group) => {
            const GroupIcon = group.icon;
            return (
              <section key={group.title} className="mb-10">
                <h2 className="text-xl font-bold text-[#F4C430] mb-5 flex items-center gap-2 pl-3 border-l-4 border-[#D4AF37]">
                  <GroupIcon className="w-5 h-5" />
                  {group.title}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {group.tools.map((tool) => {
                    const ToolIcon = tool.Icon;
                    return (
                      <Link
                        key={tool.href}
                        to={tool.href}
                        onClick={() =>
                          trackEvent('539_hub_to_tool_click', { tool: tool.href.replace('/tools/539-', '') })
                        }
                        className="group p-5 rounded-xl bg-[#0a0a0a] border border-gray-800 hover:border-yellow-500/40 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center group-hover:bg-yellow-500/20 transition-colors">
                            <ToolIcon className="w-5 h-5 text-[#D4AF37]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-sm font-bold text-white group-hover:text-[#F4C430] transition-colors">
                                {tool.title}
                              </h3>
                              {tool.badge && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#D4AF37] text-black font-bold">
                                  {tool.badge}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 leading-relaxed">{tool.desc}</p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            );
          })}

          {/* ── SEO Content Block ── */}
          <section className="mb-10 p-6 rounded-xl bg-[#0a0a0a] border border-gray-800 space-y-5">
            <h2 className="text-xl font-bold text-[#F4C430] pl-3 border-l-4 border-[#D4AF37]">
              關於今彩 539 工具集
            </h2>
            <div className="space-y-4 text-sm text-gray-400 leading-relaxed">
              <div>
                <h3 className="text-base font-bold text-white mb-2">為什麼選 cyxey.uk 的 539 工具？</h3>
                <p>
                  全網 539 工具站多數使用模擬數據或週期性手動更新。cyxey.uk 直接串接
                  <strong className="text-white">台灣彩券官方 API</strong>
                  ，每日 20:35 開獎後自動爬取最新號碼，並由
                  <strong className="text-white">Cloud Functions</strong>
                  即時計算冷熱號、遺漏值、區間分布等多維度統計。所有工具均共用同一份
                  <strong className="text-white">Firestore 即時資料庫</strong>
                  （375+ 期歷史紀錄），確保跨工具數據一致。
                </p>
              </div>
              <div>
                <h3 className="text-base font-bold text-white mb-2">推薦使用流程</h3>
                <ol className="list-decimal pl-5 space-y-1.5">
                  <li>
                    <strong className="text-white">查最新開獎</strong>
                    ：先到「
                    <Link to="/tools/539-live-draw" className="text-[#D4AF37] hover:underline">
                      即時開獎
                    </Link>
                    」確認本期號碼。
                  </li>
                  <li>
                    <strong className="text-white">看歷史走勢</strong>
                    ：用「
                    <Link to="/tools/539-trend-chart" className="text-[#D4AF37] hover:underline">
                      走勢圖
                    </Link>
                    」與「
                    <Link to="/tools/539-statistics" className="text-[#D4AF37] hover:underline">
                      統計儀表板
                    </Link>
                    」掌握冷熱趨勢。
                  </li>
                  <li>
                    <strong className="text-white">智慧選號</strong>
                    ：用「
                    <Link to="/tools/539-number-generator" className="text-[#D4AF37] hover:underline">
                      智慧選號器
                    </Link>
                    」依冷熱號權重產出建議組合。
                  </li>
                  <li>
                    <strong className="text-white">計算成本</strong>
                    ：用「
                    <Link to="/tools/539-combo-calculator" className="text-[#D4AF37] hover:underline">
                      碰數計算機
                    </Link>
                    」算連碰、立柱、天二天三的總成本與獎金期望值。
                  </li>
                  <li>
                    <strong className="text-white">開獎後對獎</strong>
                    ：用「
                    <Link to="/tools/539-checker" className="text-[#D4AF37] hover:underline">
                      對獎機器人
                    </Link>
                    」自動比對中獎結果。
                  </li>
                </ol>
              </div>
              <div>
                <h3 className="text-base font-bold text-white mb-2">工具更新頻率</h3>
                <p>
                  開獎類工具（即時開獎、歷史、走勢圖、統計）每日 20:45 自動更新；月度報告每月 1 號重算上月資料；
                  靜態計算工具（包牌、碰數、獎金計算機）邏輯固定，無需更新。
                </p>
              </div>
            </div>
          </section>

          {/* ── Related Articles ── */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-[#F4C430] mb-5 flex items-center gap-2 pl-3 border-l-4 border-[#D4AF37]">
              <BookOpen className="w-5 h-5" />
              延伸閱讀（539 攻略）
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { href: '/blog/539-guide', title: '539 完整新手攻略', desc: '玩法規則、選號技巧、投注策略一篇搞懂' },
                { href: '/blog/539-package-betting-guide', title: '539 包牌完整教學', desc: '連碰、立柱、膽拖技巧詳解' },
                { href: '/blog/539-hot-cold-numbers', title: '539 冷熱號選號攻略', desc: '用數據分析歷史開獎，提升選號命中率' },
                { href: '/blog/539-trend-chart-guide', title: '539 走勢圖完全解讀', desc: '7 種讀法、實戰判讀、版路速讀' },
              ].map((art) => (
                <Link
                  key={art.href}
                  to={art.href}
                  className="block p-4 rounded-lg border border-gray-800 hover:border-yellow-500/30 transition-colors"
                >
                  <h3 className="text-sm font-bold text-white mb-1 hover:text-[#F4C430]">{art.title}</h3>
                  <p className="text-xs text-gray-500">{art.desc}</p>
                </Link>
              ))}
            </div>
          </section>


          {/* ── Disclaimer ── */}
          <div className="text-xs text-gray-600 leading-relaxed text-center pb-8">
            <p>
              本工具集所有數據僅供參考，不構成任何投注建議。每期開獎為獨立隨機事件，歷史數據無法預測未來結果。
              請理性投注、量力而為。未滿 18 歲不得參與投注。如需確認開獎資訊，請以
              <a
                href="https://www.taiwanlottery.com/lotto/result/4_d/"
                rel="nofollow noopener"
                target="_blank"
                className="text-[#D4AF37] hover:underline"
              >
                台灣彩券官方公告
              </a>
              為準。
            </p>
          </div>
        </main>
      </div>
    </>
  );
}
